import * as AlulaSelectors from "../selectors/alula.selectors";

import { EventEmitter, Injectable } from "@angular/core";
import { AuthSelectors, AuthState } from "@app/auth/store";
import { EntityActions, EntitySelectors } from "@app/entity/store";
import { AlulaActions, DevicesActions, ProjectActions, ProjectFeatureActions } from "@app/project/store/actions";
import { RootStoreState, RouterActions } from "@app/root-store";
import { DialogActions, RouterGo, SnackbarActions } from "@app/root-store/actions";
import { ClareStatus, onlineClareStatus } from "@app/shared/model/clare-status.model";
import { Action, Store, select } from "@ngrx/store";
import { Observable, combineLatest, iif, of } from "rxjs";
import { catchError, distinctUntilChanged, filter, map, switchMap, take, takeUntil, tap } from "rxjs/operators";
import { ApiResultState, LoadingState } from "../../../root-store/api-result.state";
import { ClearProjectState, ClearProjects } from "../actions/projects.actions";

import { AddressSelectors } from "@app/address/store";
import { ClearLocationUsersState } from "@app/customer-portal/store/actions/customer-location.actions";
import { Customer } from "@app/customer/model/customer";
import { ActionPayload } from "@app/customer/store/actions";
import { Entity } from "@app/entity/model/entity";
import { CHDevice } from "@app/project/model/ch-device";
import { ClareSystem } from "@app/project/model/clare-system";
import { Project } from "@app/project/model/project";
import { ProjectListItemData } from "@app/project/model/project-list-item";
import { SecuritySystem } from "@app/project/model/security-system";
import { CHServiceService } from "@app/project/services/ch-services.service";
import { ProjectHelperService } from "@app/project/services/project-helper.service";
import { ProjectStatusService } from "@app/project/services/project-status.service";
import { ProjectSelectors } from "@app/project/store";
import { DEVICE_CLASSES } from "@app/shared/services/equator-api/ClareHomeRpc.constants";
import { HttpResponseHandler } from "@app/shared/services/http-response-handler";
import { TSLoggingService } from "@app/shared/services/ts-logging.service";
import { UserManagementActions } from "@app/user/store";
import { TypedAction } from "@ngrx/store/src/models";
import { ConfirmationAction } from "../actions/generic.action";
import { ProjectState } from "../projects.state";

export interface DispatchDeleteProjectArgs {
    project: Project;
    closeDialog?: EventEmitter<Action>;
    confirmation?: ConfirmationAction;
}

@Injectable({
    providedIn: "root",
})
export class ProjectDispatchers {
    constructor(
        private readonly store: Store<ProjectState | AuthState | RootStoreState.State>,
        private chServiceService: CHServiceService,
        //! @TODO: Service isn't being initialized in time and is making Webpack
        //!        complain.
        private readonly projectStatusService: ProjectStatusService,
        // private readonly projectService: ProjectService,
        private readonly tsLoggingService: TSLoggingService
    ) {}

    dispatch(action) {
        this.store.dispatch(action);
    }

    deleteProjectConfirmation(args: DispatchDeleteProjectArgs) {
        this.store.dispatch(new ProjectActions.DeleteProjectConfirmation(args.project, args.closeDialog, args.confirmation));
    }

    /**
     * @param existingProjectSecurityMac
     * @param uuid
     * @param unsubscribe$
     * @param isOnline
     * @deprecated
     * Use getMacAddress$
     */
    getMacAddress(existingProjectSecurityMac: string, uuid: string, unsubscribe$: Observable<void>, isOnline: boolean) {
        if (existingProjectSecurityMac) {
            return of(existingProjectSecurityMac);
        }
        if (isOnline) {
            return this.getChSecurityDevices(uuid).pipe(
                map((securityPanelDevices: CHDevice[]) => {
                    // If it does not have a securitysystem in the DB,
                    // get the device from Alula based on the MAC in the security device in CH
                    if (securityPanelDevices.length > 0) {
                        return securityPanelDevices[0].Properties["MAC Address"];
                    }
                    return "";
                }),
                takeUntil(unsubscribe$),
                catchError(err => of(err))
            );
        }
        return of(null);
    }

    getMacAddress$(existingProjectSecurityMac: string, uuid: string, isOnline: boolean) {
        if (existingProjectSecurityMac) {
            return of(existingProjectSecurityMac);
        }
        if (isOnline) {
            return this.getChSecurityDevices(uuid).pipe(
                // TODO: Remove this comment when the deprecated method is removed
                /* eslint-disable sonarjs/no-identical-functions */
                map((securityPanelDevices: CHDevice[]) => {
                    // If it does not have a securitysystem in the DB,
                    // get the device from Alula based on the MAC in the security device in CH
                    if (securityPanelDevices.length > 0) {
                        return securityPanelDevices[0].Properties["MAC Address"];
                    }
                    return "";
                }),
                /* eslint-enable sonarjs/no-identical-functions */
                distinctUntilChanged(),
                catchError(err => of(err))
            );
        }
        return of(null);
    }

    selectActiveProject() {
        return this.store.pipe(select(ProjectSelectors.getActiveProject), take(1));
    }

    selectActiveProjectStatus$(noDispatchWhenEmpty = false) {
        return this.projectStatusService.getActiveProjectStatus$(noDispatchWhenEmpty);
    }

    selectProjectAddress$(projectAddressId) {
        return this.store.pipe(select(AddressSelectors.getAddress(projectAddressId)));
    }

    getChSecurityDevices(uuid: string) {
        if (uuid) {
            return this.chServiceService.getSecurityPanel(uuid).pipe(take(1));
        }
        return of([]);
    }

    /**
     * @param mac
     * @param unsubscribe$
     * @depcrecated
     * use getAlulaDevice$
     */
    getAlulaDevice(mac: string, unsubscribe$) {
        let macAddress = mac;

        return this.store.pipe(
            select(AlulaSelectors.getAlulaDevice),
            distinctUntilChanged(),
            takeUntil(unsubscribe$),
            tap(call => {
                // If we haven't tried getting the Alula Device, get it now
                if (call?.callState === LoadingState.init && macAddress) {
                    this.store.dispatch(new AlulaActions.LoadAlulaStateItem("device"));
                    this.store.dispatch(new AlulaActions.GetAlulaDevice({ macAddress }));
                }

                // Clear the mac or else each emission will try to get it again
                macAddress = "";
            })
        );
    }

    getAlulaDevice$(mac: string) {
        let macAddress = mac;

        return this.store.pipe(
            select(AlulaSelectors.getAlulaDevice),
            distinctUntilChanged(),
            // TODO: Remove this comment when the deprecated method is removed
            /* eslint-disable sonarjs/no-identical-functions */
            tap(call => {
                // If we haven't tried getting the Alula Device, get it now
                if (call?.callState === LoadingState.init) {
                    this.store.dispatch(new AlulaActions.LoadAlulaStateItem("device"));
                    this.store.dispatch(new AlulaActions.GetAlulaDevice({ macAddress }));
                    // Clear the mac or else each emission will try to get it again
                    macAddress = "";
                }
            })
            /* eslint-enable sonarjs/no-identical-functions */
        );
    }

    upsertCustomer(action: ActionPayload & TypedAction<string>, project: Project, userInfoKey: string, closeDialog: EventEmitter<void>) {
        if (project[userInfoKey] === action.customer.id) {
            this.store.dispatch(action);
            closeDialog.emit();
        } else if (action.customer.id) {
            this.store.dispatch(
                new ProjectActions.CreateProjectUser(
                    {
                        userData: action.customer,
                        project,
                        userInfoKey,
                    },
                    closeDialog
                )
            );
        }
    }

    dispatchProjectUsers(projectId: number) {
        this.store.dispatch(new ProjectActions.GetProjectUsers(projectId));
    }

    dispatchClearLocationUsers(claresystem: ClareSystem) {
        this.store.dispatch(new ClearLocationUsersState({ claresystem }));
    }

    clearProjects() {
        this.store.dispatch(new ClearProjectState());
    }

    clearProjectArray() {
        this.store.dispatch(new ClearProjects());
    }

    navigate(url) {
        this.store.dispatch(RouterActions.routerGo({ path: url }));
    }

    dispatchUpdateControllerAction(controller: ClareSystem, project: Project, securitySystem: SecuritySystem) {
        this.store.dispatch(new ProjectActions.UpdateProjectController(controller, project, securitySystem));
    }

    dispatchCreateControllerAction(
        controller: ClareSystem,
        project: Project,
        securitySystem: SecuritySystem,
        customer?: Customer,
        entity?: Entity
    ) {
        this.store.dispatch(new ProjectActions.CreateProjectController(controller, project, securitySystem, null, customer, entity));
    }

    dispatchUpsertSecuritySystem(project: Project, securitysystem: SecuritySystem, callbackEmitter?: EventEmitter<any>) {
        this.store.dispatch(new ProjectActions.UpsertSecuritySystem({ project, securitysystem, callbackEmitter }));
    }

    dispatchUpdateProject(projectData: Project, projectId, closeDialog) {
        this.store.dispatch(new ProjectActions.UpdateProject({ projectData, id: projectId, closeDialog }));
    }

    dispatchUpdateProjectDealer(projectData, projectId, closeDialog, customer, logData) {
        this.store.dispatch(
            new ProjectActions.UpdateProjectDealer({
                projectData,
                id: projectId,
                customer,
                closeDialog,
                logData,
            })
        );
    }

    dispatchUpdateProjectSuccess(currentProject: Project) {
        this.store.dispatch(new ProjectActions.UpdateProjectSuccess(currentProject));
    }

    getActiveProjectEntity$() {
        return combineLatest([
            this.store.pipe(select(ProjectSelectors.getActiveProjectEntityState)),
            this.store.pipe(select(ProjectSelectors.getActiveProject)),
        ]).pipe(
            tap(([projectEntityState, activeProject]) => {
                if (activeProject && (!projectEntityState || projectEntityState.callState === LoadingState.init)) {
                    this.store.dispatch(new ProjectActions.GetActiveProjectEntity(activeProject.entityId));
                }
            }),
            map(([projectEntityState]) => (projectEntityState ? projectEntityState.result : null))
        );
    }

    getSelectedEntity() {
        return this.store.pipe(
            select(ProjectSelectors.getSelectedEntityState),
            map(selectedEntityState => selectedEntityState?.result)
        );
    }

    setSelectedEntity(entityId: number) {
        this.store.dispatch(new ProjectActions.SetSelectedEntity(entityId));
    }

    getAuthUser() {
        return this.store.pipe(select(AuthSelectors.selectGetUser));
    }

    loadEntites(parentId: number) {
        this.store.dispatch(EntityActions.loadSet.request({ parentId }));
    }

    getDealers() {
        return combineLatest([
            this.store.pipe(select(EntitySelectors.selectEntitiesState)),
            this.store.pipe(select(AuthSelectors.selectGetUser)),
        ]).pipe(
            filter(([entitiesState, authUser]) => !!(entitiesState && authUser)),
            tap(([entitiesState, authUser]) => {
                if (entitiesState.callState === LoadingState.init && authUser) {
                    this.store.dispatch(EntityActions.loadSet.request({ parentId: authUser.entityId }));
                }
            }),
            switchMap(([, authUser]) =>
                combineLatest([
                    this.store.pipe(select(EntitySelectors.getEntity(authUser.entityId))),
                    this.store.pipe(select(EntitySelectors.selectEntitiesArray)),
                ]).pipe(
                    filter(([entity, entities]) => !!(entity && entities)),
                    map(([entity, entities]) => this.getSubDealers(entities, entity, []))
                )
            )
        );
    }

    getSubDealers(entityArray: Entity[], parent: Entity, subDealers: Entity[]) {
        subDealers.push(parent);
        for (const entity of entityArray) {
            if (entity.parentId === parent.id) {
                this.getSubDealers(entityArray, entity, subDealers);
            }
        }
        return subDealers.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }

    showNoCoordinatesInAddressDialog(payload): void {
        this.store.dispatch(DialogActions.openDialog(payload));
    }

    loadProjects$(entityId: number): Observable<ApiResultState<ProjectListItemData[]>> {
        return this.store.pipe(
            select(ProjectSelectors.getProjectsState),
            // If we are not loading the projects, load them
            tap(projectsState => {
                if (projectsState.callState === LoadingState.init) {
                    this.store.dispatch(new ProjectActions.GetProjects({ entityId }));
                }
            })
        );
    }

    loadCentralStationProjects$(macAddresses: string[]): Observable<any> {
        return this.store.pipe(
            select(ProjectSelectors.getCentralStationProjectsState),
            // If we are not loading the projects, load them
            tap(centralStationProjectState => {
                if (centralStationProjectState.callState === LoadingState.init) {
                    this.store.dispatch(new ProjectActions.GetCentralStationProjectData(macAddresses));
                }
            }),
            map(centralStationProjects => centralStationProjects.result)
        );
    }

    loadProjectsUUIDs(entityId: number): void {
        this.store.dispatch(new ProjectActions.GetProjectsUUIDs(entityId));
    }

    loadAuthUserProjectsUUIDs$(selectedEntityId?: number): Observable<number> {
        return this.store.pipe(
            select(AuthSelectors.selectAuthUserEntityId),
            // If we have an auth entity
            filter((entityId: number) => !!entityId),
            take(1),
            tap((entityId: number) => this.loadProjectsUUIDs(selectedEntityId || entityId))
        );
    }

    loadAuthUserProjects$(): Observable<ApiResultState<ProjectListItemData[]>> {
        return this.store.pipe(
            select(AuthSelectors.selectAuthUserEntityId),
            // If we have an auth entity
            filter((entityId: number) => !!entityId),
            take(1),
            switchMap((entityId: number) => this.loadProjects$(entityId))
        );
    }

    clearActiveProject() {
        return this.store.dispatch(new ProjectActions.ClearActiveProject());
    }

    clearActiveProjectStatus() {
        return this.store.dispatch(new ProjectActions.ClearActiveProjectStatus());
    }

    clearActiveProjectUser() {
        return this.store.dispatch(UserManagementActions.UserActions.clearActiveProjectUser());
    }

    loadProjectUser(id: number) {
        this.store.dispatch(UserManagementActions.UserActions.loadActiveProjectUser({ userId: id }));
    }

    clearActiveProjectEntity() {
        return this.store.dispatch(new ProjectActions.ClearActiveProjectEntity());
    }

    moveOutCustomer() {
        return this.store.dispatch(new ProjectActions.MoveOutSuccess());
    }
    /**
     * active or inactive an suspended project
     *
     * @param doActivate
     * @param projectId
     * @param closeDialog
     */
    activateOrSuspendProject(doActivate: boolean, projectId: number, closeDialog: any) {
        this.store.dispatch(new ProjectActions.ActiveOrSuspendProject({ doActivate, projectId, closeDialog }));
    }

    removeProjectSuccess(projectData: Project) {
        this.store.dispatch(new ProjectActions.DeleteProjectSuccess(projectData));
    }

    getAlulaDeviceFromProject$(projectStatus: ClareStatus, project: Project) {
        return this.getMacAddress$(
            ProjectHelperService.getDbSecuritySystemMacAddress(project),
            ProjectHelperService.getProjectUuid(project),
            projectStatus === onlineClareStatus
        ).pipe(
            switchMap((macAddress: string) =>
                iif(() => !!macAddress, this.getAlulaDevice$(macAddress), of({ result: null, callState: LoadingState.loaded }))
            )
        );
    }

    dispatchUpdatePIN(
        uuid: string,
        deviceToEdit: CHDevice,
        dcoProperty: "updateMasterPin" | "updateInstallerPin",
        pinValue: string,
        pinType: string,
        callbackEmitter: EventEmitter<Action | RouterGo>,
        silent = false
    ) {
        const secureTokenPlaceholder = "<<secure_token_placeholder>>";
        const payload = {
            uuid,
            deviceUid: deviceToEdit.UID,
            deviceClass: DEVICE_CLASSES.INTRUSION_USER_ADMIN,
            property: dcoProperty,
            args: [secureTokenPlaceholder, pinValue],
            callbackEmitter,
        };

        this.getAuthUser()
            .pipe(take(1))
            .subscribe(user => {
                const auditPayload = {
                    payload: {
                        uuid: payload.uuid,
                        deviceUid: payload.deviceUid,
                        deviceClass: payload.deviceClass,
                        property: payload.property,
                        args: payload.args,
                    },
                    pinType,
                    staffMember: user.displayName,
                    staffEmail: user.email,
                    timestamp: new Date().getUTCDate(),
                };

                this.tsLoggingService.logEvent(payload.uuid, payload.deviceUid, "securityPIN", "update", null, auditPayload);
            });
        const action = silent
            ? new DevicesActions.UpdateSecureDeviceClassObjectOperationSilent(payload)
            : new DevicesActions.UpdateSecureDeviceClassObjectOperation(payload);
        this.dispatch(action);
    }

    dispatchExportProjectFailure() {
        const message = new HttpResponseHandler(null, null, "Export failed. Please disable any popup blockers and try again.", true);
        this.store.dispatch(
            SnackbarActions.openSnackbar({
                message,
            })
        );
    }
    clearProjectFeatureState() {
        this.store.dispatch(ProjectFeatureActions.clear());
    }
}
